import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Airplane, Calendar } from "@phosphor-icons/react";
import { useTranslation } from "react-i18next";

function timeUntil(iso) {
    if (!iso) return null;
    const target = new Date(iso).getTime();
    if (Number.isNaN(target)) return null;
    const diff = target - Date.now();
    if (diff <= 0) return { days: 0, hours: 0, minutes: 0, passed: true };
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);
    const minutes = Math.floor((diff / (1000 * 60)) % 60);
    return { days, hours, minutes, passed: false };
}

export default function TripCountdown({ tripDate, destination, compact = false }) {
    const { t } = useTranslation();
    const [tu, setTu] = useState(() => timeUntil(tripDate));

    useEffect(() => {
        if (!tripDate) return;
        setTu(timeUntil(tripDate));
        const id = setInterval(() => setTu(timeUntil(tripDate)), 60_000);
        return () => clearInterval(id);
    }, [tripDate]);

    if (!tripDate || !tu) return null;

    if (tu.passed) {
        return (
            <div
                className={compact ? "px-3 py-2 rounded-full text-xs inline-flex items-center gap-2" : "rounded-2xl p-4 flex items-center gap-3"}
                style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}
                data-testid="trip-countdown-passed"
            >
                <Calendar size={12} color="#D4AF37" />
                <span className="text-white/60 text-xs">{t("tripCountdown.complete")}</span>
            </div>
        );
    }

    if (compact) {
        return (
            <div
                className="px-3 py-1.5 rounded-full text-[11px] inline-flex items-center gap-2"
                style={{ background: "rgba(244,162,143,0.14)", border: "1px solid rgba(244,162,143,0.4)", color: "#F4A28F" }}
                data-testid="trip-countdown-compact"
            >
                <Airplane size={11} weight="fill" />
                <span className="font-semibold uppercase tracking-widest">
                    {t("tripCountdown.compactToGo", { days: tu.days, hours: tu.hours })}
                </span>
            </div>
        );
    }

    return (
        <motion.div
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
            className="rounded-2xl p-4 md:p-5 relative overflow-hidden"
            style={{ background: "linear-gradient(135deg, rgba(244,162,143,0.18), rgba(11,19,32,0.6))", border: "1px solid rgba(244,162,143,0.4)" }}
            data-testid="trip-countdown"
        >
            <div className="flex items-center gap-2 mb-2">
                <div className="w-7 h-7 rounded-full grid place-items-center" style={{ background: "rgba(244,162,143,0.2)" }}>
                    <Airplane size={12} weight="fill" color="#F4A28F" />
                </div>
                <div className="overline" style={{ color: "#F4A28F", letterSpacing: "0.25em" }}>{t("tripCountdown.header")}</div>
                {destination && (
                    <div className="ml-auto text-[11px] text-white/50">→ {destination}</div>
                )}
            </div>
            <div className="flex items-baseline gap-4">
                <NumBlock value={tu.days} label={t("tripCountdown.days")} />
                <NumBlock value={tu.hours} label={t("tripCountdown.hours")} />
                <NumBlock value={tu.minutes} label={t("tripCountdown.min")} />
            </div>
        </motion.div>
    );
}

function NumBlock({ value, label }) {
    return (
        <div>
            <div className="font-serif text-4xl md:text-5xl text-white tracking-tighter leading-none">
                {String(value).padStart(2, "0")}
            </div>
            <div className="text-[9px] uppercase tracking-[0.25em] text-white/50 mt-1">{label}</div>
        </div>
    );
}
